import { organizationOnLetter } from '@/lib/medcom/letterDisplay';
import { signatureOrganizationName } from '@/lib/medcom/sorEntityName';

/**
 * A message segment that Videresend shows read-only.
 * The new text is typed underneath. These segments stay as they were.
 */

export type PriorSegmentSignature = {
  name: string | null;
  role: string | null;
  phone: string | null;
  organization: string | null;
};

export type PriorSegment = {
  key: string;
  /** Payload identifier in the letter. Null when the segment cannot be picked for a forward. */
  segmentId: string | null;
  kind: 'text' | 'attachment';
  text: string | null;
  title: string | null;
  timestamp: string | null;
  signature: PriorSegmentSignature | null;
};

type LetterSource = {
  textSegments?: unknown;
  attachments?: unknown;
  senderOrganization?: unknown;
  senderSor?: unknown;
  receiverOrganization?: unknown;
  receiverSor?: unknown;
};

type KnownSegmentKind = 'string' | 'attachment';

type MessageRef = {
  id: string;
  direction: 'in' | 'out';
  receivedAt: string | null;
  createdAt: string;
};

/** The letter being forwarded, otherwise the assembled thread. Never throws. */
export function priorSegmentsForForward(input: {
  letter: LetterSource | null | undefined;
  threadSegments: unknown;
}): PriorSegment[] {
  const fromLetter = input.letter ? priorSegmentsFromLetter(input.letter) : [];
  if (fromLetter.length > 0) return fromLetter;
  return priorSegmentsFromThread(input.threadSegments);
}

export function priorSegmentsFromLetter(letter: LetterSource): PriorSegment[] {
  const parties = [
    { sorId: blank(letter.senderSor), name: blank(letter.senderOrganization) },
    { sorId: blank(letter.receiverSor), name: blank(letter.receiverOrganization) },
  ];
  const texts = records(letter.textSegments).map((segment, index) =>
    textSegment(segment, index, parties),
  );
  const attachments = records(letter.attachments).map((attachment, index) =>
    attachmentSegment(attachment, index),
  );
  return [...texts, ...attachments];
}

export function priorSegmentsFromThread(segments: unknown): PriorSegment[] {
  const result: PriorSegment[] = [];
  records(segments).forEach((segment, index) => {
    const kind = knownKind(segment.kind);
    if (!kind) return;
    switch (kind) {
      case 'string': {
        const text = textContent(segment.text).trim();
        if (!text) break;
        const sender = recordOf(segment.sender);
        result.push({
          key: segmentKey(segment, index),
          segmentId: null,
          kind: 'text',
          text,
          title: null,
          timestamp: timestampOf(segment.timestamp),
          signature: signatureOrNull({
            name: blank(sender?.practitionerName),
            role: blank(sender?.practitionerRoleText),
            phone: blank(sender?.phone),
            organization: organizationOnLetter(
              blank(sender?.organizationName),
              blank(sender?.sorId),
            ),
          }),
        });
        break;
      }
      case 'attachment': {
        const attachment = recordOf(segment.attachment);
        result.push({
          key: segmentKey(segment, index),
          segmentId: null,
          kind: 'attachment',
          text: null,
          title: blank(attachment?.title),
          timestamp: timestampOf(segment.timestamp),
          signature: null,
        });
        break;
      }
      default: {
        const unexpected: never = kind;
        void unexpected;
        break;
      }
    }
  });
  return result;
}

/** A forward can pick parts only when the letter has several and each one has an identifier. */
export function canChooseSegments(segments: PriorSegment[]): boolean {
  return segments.length > 1 && segments.every((segment) => segment.segmentId !== null);
}

/**
 * Identifiers to send as forwardSegmentIds, or undefined to forward the whole letter.
 * An empty list means nothing is chosen.
 */
export function chosenSegmentIds(
  segments: PriorSegment[],
  leftOut: ReadonlySet<string>,
): string[] | undefined {
  if (!canChooseSegments(segments)) return undefined;
  const chosen = segments.filter((segment) => !leftOut.has(segment.key));
  if (chosen.length === segments.length) return undefined;
  return chosen.flatMap((segment) => (segment.segmentId ? [segment.segmentId] : []));
}

/** Inbound letter when one exists, otherwise the newest message in the thread. */
export function newestMessageId(messages: MessageRef[], preferInbound: boolean): string | null {
  const inbound = messages.filter((message) => message.direction === 'in');
  const pool = preferInbound && inbound.length > 0 ? inbound : messages;
  const sorted = [...pool].sort((left, right) => {
    const leftAt = left.receivedAt ?? left.createdAt;
    const rightAt = right.receivedAt ?? right.createdAt;
    return rightAt.localeCompare(leftAt);
  });
  return sorted[0]?.id ?? null;
}

function textSegment(
  segment: Record<string, unknown>,
  index: number,
  parties: Array<{ sorId: string | null; name: string | null }>,
): PriorSegment {
  const signature = recordOf(segment.signature);
  return {
    key: `text-${index}`,
    segmentId: blank(segment.segmentId),
    kind: 'text',
    text: textContent(segment.text) || null,
    title: null,
    timestamp: timestampOf(segment.timestamp) ?? timestampOf(signature?.at),
    signature: signatureFromBundle(signature, parties),
  };
}

function attachmentSegment(attachment: Record<string, unknown>, index: number): PriorSegment {
  return {
    key: `attachment-${index}`,
    segmentId: blank(attachment.segmentId),
    kind: 'attachment',
    text: null,
    title: blank(attachment.title),
    timestamp:
      timestampOf(attachment.authoredAt) ?? timestampOf(attachment.creation),
    signature: signatureOrNull({
      name: blank(attachment.authorName),
      role: null,
      phone: blank(attachment.authorPhone),
      organization: null,
    }),
  };
}

function signatureFromBundle(
  signature: Record<string, unknown> | null,
  parties: Array<{ sorId: string | null; name: string | null }>,
): PriorSegmentSignature | null {
  if (!signature) return null;
  return signatureOrNull({
    name: blank(signature.name),
    role: blank(signature.role),
    phone: blank(signature.phone),
    organization: signatureOrganizationName(blank(signature.organization), parties),
  });
}

function signatureOrNull(signature: PriorSegmentSignature): PriorSegmentSignature | null {
  if (!signature.name && !signature.role && !signature.phone && !signature.organization) {
    return null;
  }
  return signature;
}

function knownKind(kind: unknown): KnownSegmentKind | null {
  if (kind === 'string' || kind === 'attachment') return kind;
  return null;
}

function records(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object',
  );
}

function recordOf(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

function segmentKey(segment: Record<string, unknown>, index: number): string {
  return typeof segment.segmentId === 'string' && segment.segmentId
    ? segment.segmentId
    : `segment-${index}`;
}

function timestampOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function textContent(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function blank(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
